import { type ReactNode, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export interface DialogProps {
  open: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  disableOverlayClose?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

/**
 * Dialog/Modal — accessible modal overlay via React Portal.
 * Traps focus, handles Escape, restores focus to the trigger on close.
 */
export function Dialog({
  open,
  onClose,
  onOpenChange,
  children,
  disableOverlayClose,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
}: DialogProps) {
  const handleClose = () => {
    onClose?.();
    onOpenChange?.(false);
  };
  const contentRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
        return;
      }

      if (e.key === "Tab" && contentRef.current) {
        const focusableElements =
          contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
        const focusable = Array.from(focusableElements).filter(
          (el) => el.offsetParent !== null,
        );

        if (focusable.length === 0) return;

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!open || !contentRef.current) return;

    const focusableElements =
      contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
    const focusable = Array.from(focusableElements).filter(
      (el) => el.offsetParent !== null,
    );

    requestAnimationFrame(() => {
      if (focusable.length > 0) {
        focusable[0].focus();
      } else if (contentRef.current) {
        contentRef.current.focus();
      }
    });

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  const handleOverlayClick = () => {
    if (!disableOverlayClose) {
      handleClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onKeyDown={handleKeyDown}
    >
      <div
        className="absolute inset-0 bg-espresso/60 backdrop-blur-[2px] motion-safe:animate-overlay-in"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />
      <div
        ref={contentRef}
        className="relative z-10 max-h-[90vh] overflow-auto focus:outline-none motion-safe:animate-dialog-in"
        tabIndex={-1}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export interface DialogContentProps {
  children: ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
}

export function DialogContent({ children, className, size = "md" }: DialogContentProps) {
  return (
    <div
      className={cn(
        "bg-bg-card rounded-xl shadow-xl border border-border",
        "w-[calc(100vw-2rem)] mx-4",
        {
          "sm:w-[400px]": size === "sm",
          "sm:w-[500px]": size === "md",
          "sm:w-[600px]": size === "lg",
          "sm:w-[800px]": size === "xl",
          "sm:w-[1100px]": size === "2xl",
        },
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface DialogHeaderProps {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
}

export function DialogHeader({ children, className, onClose }: DialogHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between px-6 py-4 border-b border-border", className)}>
      <div className="font-display text-lg font-semibold tracking-tight text-text-primary">
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="text-text-muted hover:text-text-primary transition-colors p-1 rounded-md hover:bg-bg-hover"
          aria-label="Close dialog"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
    </div>
  );
}

export interface DialogBodyProps {
  children: ReactNode;
  className?: string;
}

export function DialogBody({ children, className }: DialogBodyProps) {
  return <div className={cn("px-6 py-4", className)}>{children}</div>;
}

export interface DialogFooterProps {
  children: ReactNode;
  className?: string;
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div className={cn("flex items-center justify-end gap-3 rounded-b-xl px-6 py-4 border-t border-border bg-bg-body", className)}>
      {children}
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  isLoading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "primary",
  isLoading,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>{title}</DialogHeader>
        <DialogBody>
          <p className="text-text-secondary">{message}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
